import { useState, type FormEvent } from "react";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { submitPublicTicket } from "./api";

/**
 * Raise a ticket without an account. Posts to the public intake endpoint (no
 * auth). Includes a hidden honeypot field the server drops if filled.
 */
export function PublicTicketForm({ onBack }: { onBack: () => void }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    description: "",
    website: "", // honeypot
  });
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await submitPublicTicket({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        subject: form.subject.trim(),
        description: form.description.trim(),
        website: form.website,
        screenshot,
      });
      setReference(res.reference || "received");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not submit your request.");
    } finally {
      setSubmitting(false);
    }
  }

  const input =
    "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring";

  if (reference) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center">
        <CheckCircle2 className="mx-auto h-8 w-8 text-sla-ok" aria-hidden="true" />
        <h2 className="mt-3 font-semibold">Request received</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {reference !== "received" ? (
            <>
              Your reference is{" "}
              <span className="font-medium text-foreground">{reference}</span>. We'll be in touch by
              email.
            </>
          ) : (
            "Thanks — our team will be in touch by email."
          )}
        </p>
        <button
          type="button"
          onClick={onBack}
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-border bg-card p-6" noValidate>
      <p className="text-sm text-muted-foreground">
        No account needed — tell us what you need help with and we'll email you back.
      </p>

      {error && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label htmlFor="p_name" className="text-sm font-medium">Your name</label>
          <input id="p_name" className={input} value={form.name} onChange={(e) => set("name")(e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="p_phone" className="text-sm font-medium">Phone (optional)</label>
          <input id="p_phone" className={input} value={form.phone} onChange={(e) => set("phone")(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label htmlFor="p_email" className="text-sm font-medium">Email</label>
          <input id="p_email" type="email" autoComplete="email" className={input} value={form.email} onChange={(e) => set("email")(e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="p_company" className="text-sm font-medium">Company</label>
          <input id="p_company" className={input} value={form.company} onChange={(e) => set("company")(e.target.value)} required />
        </div>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="p_subject" className="text-sm font-medium">Subject</label>
        <input id="p_subject" className={input} value={form.subject} onChange={(e) => set("subject")(e.target.value)} required />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="p_desc" className="text-sm font-medium">How can we help?</label>
        <textarea
          id="p_desc"
          rows={4}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={form.description}
          onChange={(e) => set("description")(e.target.value)}
          required
        />
        <p className="text-xs text-muted-foreground">
          {form.description.trim().length < 10
            ? `Please add a little more detail — at least 10 characters (${form.description.trim().length}/10).`
            : "Thanks — that's enough detail to get started."}
        </p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="p_shot" className="text-sm font-medium">Screenshot (optional, max 2 MB)</label>
        <input
          id="p_shot"
          type="file"
          accept="image/*,.pdf"
          className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-accent file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-accent/80"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            if (f && f.size > 2 * 1024 * 1024) {
              setError("The screenshot must be 2 MB or smaller.");
              setScreenshot(null);
              e.target.value = "";
              return;
            }
            setError(null);
            setScreenshot(f);
          }}
        />
        {screenshot && (
          <p className="text-xs text-muted-foreground">
            Attached: {screenshot.name} ({Math.round(screenshot.size / 1024)} KB)
          </p>
        )}
      </div>

      {/* Honeypot: hidden from real users; bots fill it and get silently dropped. */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        value={form.website}
        onChange={(e) => set("website")(e.target.value)}
      />

      <button
        type="submit"
        disabled={submitting || !form.name || !form.email || !form.company.trim() || !form.subject || form.description.trim().length < 10}
        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {submitting ? "Submitting…" : "Submit request"}
      </button>

      <button type="button" onClick={onBack} className="inline-flex w-full items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to sign in
      </button>
    </form>
  );
}
